import styled from "@emotion/styled";

import { paper, fonts, shade } from "../theme";
import type { GameSettingsData } from "../protocol";

import {
    MsScreen, MsBody, MsFooter,
    MsDisplay, MsEyebrow, MsRule, MsRuleRed,
    MsPaper, MsIndex, MsTape,
    MsBtn, MsBackButton,
    MaskHeadline,
    PaperToggle, PaperChips, MsFieldLabel,
} from "../ms";

const SettingsBody = styled(MsBody)`
    gap: 18px;
    padding-bottom: 24px;
`;

const SettingsHeadline = styled(MsDisplay)`
    font-size: 38px;
    line-height: 0.98;
`;

const HeadlineRule = styled(MsRuleRed)`
    margin-top: 14px;
`;

const SettingsIndex = styled(MsIndex)`
    padding: 34px 22px 22px;
`;

const FieldRule = styled(MsRule)`
    margin: 18px 0 14px;
    opacity: 0.4;
`;

const SwitchesPaper = styled(MsPaper)`
    padding: 6px 22px;
`;

const ReshuffleSection = styled.div`
    padding: 14px 0;
    border-top: 1px dashed ${shade(0.18)};
`;

export type RoomSettings = GameSettingsData & { totalRounds: number | null };

const ROUND_OPTIONS = [3, 5, 7, 10, null] as const;
const TIMER_OPTIONS = [30, 60, 120, 180, 300] as const;
const ARTICLE_OPTIONS = [2, 3, 4, 5] as const;
const RESHUFFLE_OPTIONS = [1, 2, 3] as const;

interface Props {
    settings: RoomSettings;
    onChange: (settings: RoomSettings) => void;
    /** Footer CTA label — defaults to "Done" so it works as a modal sheet. */
    ctaLabel?: string;
    onConfirm: () => void;
    onBack: () => void;
    /** When inGame, settings can't be edited — show read-only summary. */
    readOnly?: boolean;
}

/**
 * House Rules — "Editor's Desk" clipboard. Only surfaces the settings the
 * server actually accepts (timer, articles per player, total rounds,
 * reshuffle controls). Other "settings" from the design (source packs,
 * vote-time, late-joiners, etc.) are intentionally absent until the
 * protocol supports them.
 */
export default function PaperRoomSettings({
    settings, onChange, ctaLabel = "Done", onConfirm, onBack, readOnly,
}: Props) {
    const set = <K extends keyof RoomSettings>(key: K, value: RoomSettings[K]) =>
        onChange({ ...settings, [key]: value });

    const timerToDisplay = (s: number) => s >= 60 ? `${s / 60}m` : `${s}s`;

    return (
        <MsScreen>
            <SettingsBody>
                <MsBackButton onClick={onBack} />

                <div>
                    <SettingsHeadline>
                        <MaskHeadline>Set the house rules.</MaskHeadline>
                    </SettingsHeadline>
                    <HeadlineRule />
                </div>

                {/* Length & timing — clipboard card with red top margin */}
                <SettingsIndex drop r1="-0.8deg">
                    <MsTape placement="tl" />

                    <MsFieldLabel>
                        <MsEyebrow>Rounds</MsEyebrow>
                        <em>
                            {settings.totalRounds === null
                                ? "endless"
                                : `about ${Math.max(2, Math.round(settings.totalRounds * 2.5))} min`}
                        </em>
                    </MsFieldLabel>
                    <PaperChips
                        options={[...ROUND_OPTIONS] as (number | null)[]}
                        labels={ROUND_OPTIONS.map(r => r === null ? "∞" : String(r))}
                        value={settings.totalRounds}
                        disabled={readOnly}
                        onChange={(v) => !readOnly && set("totalRounds", v as number | null)}
                    />

                    <FieldRule />

                    <MsFieldLabel>
                        <MsEyebrow>Time to build a misquote</MsEyebrow>
                    </MsFieldLabel>
                    <PaperChips
                        options={[...TIMER_OPTIONS] as number[]}
                        labels={TIMER_OPTIONS.map(timerToDisplay)}
                        value={settings.timerDurationSeconds}
                        disabled={readOnly}
                        onChange={(v) => !readOnly && set("timerDurationSeconds", v)}
                    />

                    <FieldRule />

                    <MsFieldLabel>
                        <MsEyebrow>Articles per player</MsEyebrow>
                        <em>more = harder to pick</em>
                    </MsFieldLabel>
                    <PaperChips
                        options={[...ARTICLE_OPTIONS] as number[]}
                        value={settings.articlesPerPlayer}
                        disabled={readOnly}
                        onChange={(v) => !readOnly && set("articlesPerPlayer", v)}
                    />
                </SettingsIndex>

                {/* Switches */}
                <SwitchesPaper drop r1="-0.4deg">
                    <SwitchRow
                        title="New articles every round"
                        hint="Players get fresh topics rather than keeping their hand."
                        on={settings.repickArticlesEachRound}
                        disabled={readOnly}
                        onChange={(v) => !readOnly && set("repickArticlesEachRound", v)}
                        first
                    />
                    <SwitchRow
                        title="Allow reshuffles"
                        hint="Let players swap a topic they don't like."
                        on={settings.allowArticleReshuffle}
                        disabled={readOnly}
                        onChange={(v) => {
                            if (readOnly) return;
                            onChange({
                                ...settings,
                                allowArticleReshuffle: v,
                                maxReshuffles: v ? Math.max(1, settings.maxReshuffles) : 0,
                            });
                        }}
                    />

                    {settings.allowArticleReshuffle && (
                        <ReshuffleSection>
                            <MsFieldLabel>
                                <MsEyebrow>Max reshuffles per game</MsEyebrow>
                            </MsFieldLabel>
                            <PaperChips
                                options={[...RESHUFFLE_OPTIONS] as number[]}
                                value={settings.maxReshuffles as 1 | 2 | 3}
                                disabled={readOnly}
                                onChange={(v) => !readOnly && set("maxReshuffles", v)}
                            />
                        </ReshuffleSection>
                    )}
                </SwitchesPaper>
            </SettingsBody>
            <MsFooter>
                <MsBtn variant="red" onClick={onConfirm}>{ctaLabel}</MsBtn>
            </MsFooter>
        </MsScreen>
    );
}

const SwitchRowOuter = styled.div<{ first?: boolean }>`
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 14px;
    padding: 14px 0;
    border-top: ${p => p.first ? "0" : `1px dashed ${shade(0.18)}`};
`;

const SwitchTitle = styled.div`
    font-family: ${fonts.serif};
    font-weight: 700;
    font-size: 17px;
    letter-spacing: -0.005em;
`;

const SwitchHint = styled.div`
    font-family: ${fonts.serif};
    font-style: italic;
    font-size: 13px;
    color: ${paper.inkSoft};
    margin-top: 2px;
`;

interface SwitchRowProps {
    title: string;
    hint: string;
    on: boolean;
    onChange: (next: boolean) => void;
    disabled?: boolean;
    first?: boolean;
}

function SwitchRow({ title, hint, on, onChange, disabled, first }: SwitchRowProps) {
    return (
        <SwitchRowOuter first={first}>
            <div>
                <SwitchTitle>{title}</SwitchTitle>
                <SwitchHint>{hint}</SwitchHint>
            </div>
            <PaperToggle on={on} onChange={onChange} disabled={disabled} aria-label={title} />
        </SwitchRowOuter>
    );
}

